<script setup>
const props = defineProps({
    textareaConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                label: rawValue => typeof rawValue?.label === 'string',
                id: rawValue => typeof rawValue?.id === 'string',
                name: rawValue => typeof rawValue?.name === 'string',
                readonly: rawValue => rawValue?.readonly === undefined || typeof rawValue?.readonly === 'boolean',
                required: rawValue => rawValue?.required === undefined || typeof rawValue?.required === 'boolean',
                disabled: rawValue => rawValue?.disabled === undefined || typeof rawValue?.disabled === 'boolean',
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue?.placeholder === 'string',
                helperText: rawValue => rawValue?.helperText === undefined || typeof rawValue?.helperText === 'string',
                val: rawValue => rawValue?.val === undefined || typeof rawValue?.val === 'string' || typeof rawValue?.val === 'object',
                maxLength: rawValue => rawValue?.maxLength === undefined || typeof rawValue?.maxLength === 'number',
                height: rawValue => rawValue?.height === undefined || typeof rawValue?.height === 'number',
                validationRules: rawValue => rawValue?.validationRules === undefined || typeof rawValue?.validationRules === 'object',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Creates a form field with its validation state, you will use this inside your custom input components.
const { value, errorMessage, meta, resetField } = useField(() =>
    props.textareaConfig?.name, props.textareaConfig?.validationRules,
{ initialValue: '' }
)

// Show error + success classes in case fields have validation rules
const requiresValidation = computed(() => props.textareaConfig?.validationRules !== undefined)

const formFieldClass = computed(() => {
    return {
        'form-error': !meta.valid && meta.dirty && requiresValidation.value,
        'form-success': meta.valid && meta.dirty && requiresValidation.value
    }
})

// Change initial value with dynamic data of input field
resetField({
    value: props.textareaConfig?.val
})

// Use watcher to update data with dynamic values (seems to be necessary...?)
watch(() => props.textareaConfig?.val, () => {
    resetField({
        value: props.textareaConfig?.val
    })
})

// Compute height
const height = computed(() => props.textareaConfig.height ? `${props.textareaConfig.height}px` : '120px')

// Compute helper config
const formHelperConfig = computed(() => {
    return {
        helperText: props.textareaConfig.helperText,
        maxLength: props.textareaConfig.maxLength,
        value: value.value,
    }
})
</script>

<template>
    <div class="form-input-textarea-wrapper" :class="formFieldClass">
        <label class="form-input-textarea-label" :class="textareaConfig.required ? 'required' : ''">
            {{ textareaConfig.label }}:
            <span v-if="textareaConfig.required" class="asterisk-icon">*</span>
            <span v-else class="optional">(optional)</span>
        </label>

        <div class="form-input-textarea-container">
            <textarea
                v-model="value"
                class="form-input-textarea"
                :readonly="textareaConfig.readonly"
                :required="textareaConfig.required"
                :name="textareaConfig.name"
                :disabled="textareaConfig.disabled"
                :maxLength="textareaConfig.maxLength"
                :placeholder="textareaConfig.placeholder"
                rows="4"
                cols="50"
            />
            <LoadingSpinner v-if="textareaConfig.pending" class="form-loader" />
        </div>

        <FormFieldMessage
            :msg-config="{
                message: errorMessage,
                error: errorMessage && meta.dirty,
            }"
        />

        <FormHelper v-if="textareaConfig.helperText || textareaConfig.maxLength" :form-helper-config="formHelperConfig" />
    </div>
</template>

<style lang="scss">
.form-input-textarea-wrapper {
    position: relative;
}

.form-input-textarea-container {
    position: relative;
}

.form-input-textarea-label {
    @include form-label;
}

.form-input-textarea {
    font-family: $font-body;
    padding: 1rem;
    scroll-padding-block: 1rem;
    height: v-bind('height');
    display: flex;
    align-items: center;
    width: 100%;
    appearance: none;
    outline: 0;
    border-radius: 10px;
    background: $form-input-text-bg;
    color: $form-input-text;
    border: $form-input-text-border 2px solid;
    line-height: px2rem(24);
    font-size: px2rem(18);
    text-size-adjust: none;
    transition: border-color 400ms ease, background-color 400ms ease;
    @include hide-scrollbar;

    &:focus,
    &:active {
        border-color: $form-input-text-border-active;
    }

    &::placeholder {
        color: $form-input-placeholder;
        opacity: 1;
    }

    &[readonly] {
        opacity: 0.4;
    }
}
</style>
